import { useCallback, useContext, useEffect, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import BodyText from 'LuxKit/Typography/BodyText'
import {
  getElementLogicalInlineStartOffset,
  scrollToLogicalPosition,
} from 'LuxKit/Carousel/utils'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getFlashCalendarLosDurations } from 'selectors/offerPage/flashOfferLosCalendar'
import { DURATION_CAROUSEL_PROPS } from '../DurationTabs/durationCarouselLayout'
import { DurationCaret, DurationOption, DurationOptionContent } from '../DurationTabs/durationOptionStyles'

export function getCenteredScrollLeft(
  tileStart: number,
  tileWidth: number,
  viewportWidth: number,
  maxScroll: number,
): number {
  const unclamped = tileStart - ((viewportWidth - tileWidth) / 2)
  const max = Math.max(0, maxScroll)
  return Math.min(Math.max(unclamped, 0), max)
}

interface Props {
  durations: Array<number>
  selectedDuration: number
  onSelect: (duration: number) => void
}

export function FlashOfferLosCarouselView(props: Props) {
  const { durations, selectedDuration, onSelect } = props
  const intl = useIntl()
  const carouselRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const carousel = carouselRef.current
    if (!carousel) return

    const centerSelectedTile = () => {
      const tile = carousel.querySelector<HTMLElement>(`[data-los-duration="${selectedDuration}"]`)
      if (!tile) return

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const tileStart = getElementLogicalInlineStartOffset(tile, carousel)
      const maxScroll = Math.max(0, carousel.scrollWidth - carousel.clientWidth)
      scrollToLogicalPosition(carousel, {
        left: getCenteredScrollLeft(tileStart, tile.offsetWidth, carousel.clientWidth, maxScroll),
        behavior: reduceMotion ? 'auto' : 'smooth',
      })
    }

    centerSelectedTile()
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(centerSelectedTile)
    observer.observe(carousel)
    return () => observer.disconnect()
  }, [selectedDuration])

  const onTileClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const duration = Number(event.currentTarget.dataset.duration)
    if (!durations.includes(duration)) return
    onSelect(duration)
  }, [durations, onSelect])

  if (durations.length < 2) return null

  return (
    <CardCarousel
      ref={carouselRef}
      {...DURATION_CAROUSEL_PROPS}
      snap={undefined}
      gap={12}
      gutterSize={0}
      data-testid="flash-offer-los-carousel"
    >
      {durations.map(duration => {
        const selected = duration === selectedDuration
        const nightsLabel = intl.formatMessage({
          id: 'fFvCQ5ZkESx9NB3FFhOW',
          defaultMessage: '{count, plural, one {# night} other {# nights}}',
          description: 'Stay length shown on a Flash Offer calendar length-of-stay tile',
        }, { count: duration })
        return (
          <DurationOption
            key={duration}
            type="button"
            data-duration={duration}
            aria-pressed={selected}
            onClick={onTileClick}
            data-los-duration={duration}
            className={clsx({ selected })}
            data-testid={`flash-offer-los-${duration}`}
          >
            <DurationOptionContent>
              <BodyText variant="medium" weight="bold" wrap="no-wrap">{nightsLabel}</BodyText>
              <DurationCaret className={clsx({ visible: selected })} />
            </DurationOptionContent>
          </DurationOption>
        )
      })}
    </CardCarousel>
  )
}

export function FlashOfferLosCarousel(props: {
  durations: Array<number>
  selectedDuration: number
}) {
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const { durations, selectedDuration } = props

  const onSelect = useCallback((duration: number) => {
    if (duration === selectedDuration) return
    dispatch({ type: OfferPageActions.setDuration, duration })
    trackEvent('DURATION_PRESSED')
  }, [dispatch, selectedDuration, trackEvent])

  return (
    <FlashOfferLosCarouselView
      durations={durations}
      selectedDuration={selectedDuration}
      onSelect={onSelect}
    />
  )
}

export default withOfferPageAndReduxState((state) => ({
  durations: getFlashCalendarLosDurations(state),
  selectedDuration: state.duration,
}), FlashOfferLosCarousel)
